import { Inbox } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { EmptyState } from "@/components/shared/empty-state";
import { formatCurrencyEUR } from "@/lib/utils";
import {
  MDF_REQUEST_STATUS_LABEL,
  type MdfRequestStatus,
} from "@colab/shared";
import { useFundRoi } from "./use-fund-roi";

// Sub-bucket 15 — port of /app/admin/mdf/[id]/_components/FundRoiTab.tsx.
// MetricCards (committed / spent / revenue / ROI %) + per-campaign breakdown
// table. Server-side aggregation lives in services/mdf.ts::loadFundRoi
// (already in place from Phase 4); the new GET endpoint wraps it.
//
// Only campaigns with attributed closed-won deals appear in the breakdown
// (parity with legacy `loadFundRoi`'s filter); the Requests tab carries the
// full inventory.

const STATUS_VARIANT: Record<
  MdfRequestStatus,
  "default" | "outline" | "secondary"
> = {
  pending: "outline",
  approved: "default",
  spent: "secondary",
  declined: "outline",
};

export function FundRoiTab({ fundId }: { fundId: string }) {
  const query = useFundRoi(fundId);

  if (query.isLoading) {
    return (
      <div className="space-y-5">
        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
          {[0, 1, 2, 3].map((i) => (
            <Skeleton key={i} className="h-20 rounded-xl" />
          ))}
        </div>
        <Skeleton className="h-40 w-full rounded-xl" />
      </div>
    );
  }

  if (query.isError || !query.data) {
    return (
      <EmptyState
        icon={Inbox}
        title="Couldn't load ROI"
        description={query.error?.message ?? "Try refreshing."}
      />
    );
  }

  const roi = query.data;
  const roiDisplay =
    roi.roi_pct === null
      ? "—"
      : `${roi.roi_pct >= 0 ? "+" : ""}${roi.roi_pct.toFixed(0)}%`;

  return (
    <div className="space-y-5">
      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
        <MetricCard
          label="Total committed"
          value={formatCurrencyEUR(roi.total_committed_eur)}
          hint="Approved + spent"
        />
        <MetricCard
          label="Total spent"
          value={formatCurrencyEUR(roi.total_spent_eur)}
          hint="MDF requests marked spent"
        />
        <MetricCard
          label="Revenue attributed"
          value={formatCurrencyEUR(roi.revenue_eur)}
          hint="Closed-won deals linked to this fund"
        />
        <MetricCard
          label="ROI"
          value={roiDisplay}
          hint={
            roi.total_spent_eur === 0
              ? "Mark a request spent to start measuring"
              : "(Revenue − Spent) ÷ Spent"
          }
        />
      </div>

      <div className="rounded-xl border bg-card">
        <div className="border-b px-4 py-3">
          <h3 className="font-display text-sm font-semibold tracking-tight">
            Per-campaign breakdown
          </h3>
          <p className="mt-0.5 text-xs text-muted-foreground">
            Only campaigns with attributed closed-won deals appear here.
          </p>
        </div>
        {roi.breakdown.length === 0 ? (
          <div className="px-4 py-10 text-center text-sm text-muted-foreground">
            No revenue attributed yet. Link closed-won deals to MDF campaigns
            via the deal drawer to see the ROI loop close.
          </div>
        ) : (
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b bg-muted/20 text-left text-[11px] font-medium uppercase tracking-wider text-muted-foreground">
                <th className="px-4 py-2.5">Campaign</th>
                <th className="px-4 py-2.5">Status</th>
                <th className="px-4 py-2.5 text-right">Amount</th>
                <th className="px-4 py-2.5 text-right">Deals</th>
                <th className="px-4 py-2.5 text-right">Revenue</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border/70">
              {roi.breakdown.map((row) => (
                <tr key={row.request_id}>
                  <td className="px-4 py-3 font-medium">{row.campaign_name}</td>
                  <td className="px-4 py-3">
                    <Badge variant={STATUS_VARIANT[row.status]}>
                      {MDF_REQUEST_STATUS_LABEL[row.status]}
                    </Badge>
                  </td>
                  <td className="px-4 py-3 text-right tabular-nums">
                    {formatCurrencyEUR(row.amount_eur)}
                  </td>
                  <td className="px-4 py-3 text-right tabular-nums">
                    {row.deals_count}
                  </td>
                  <td className="px-4 py-3 text-right tabular-nums">
                    {formatCurrencyEUR(row.revenue_eur)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}

function MetricCard({
  label,
  value,
  hint,
}: {
  label: string;
  value: string;
  hint: string;
}) {
  return (
    <div className="rounded-xl border bg-card p-4">
      <p className="text-[11px] font-medium uppercase tracking-wider text-muted-foreground">
        {label}
      </p>
      <p className="mt-1 font-display text-2xl font-semibold tabular-nums">
        {value}
      </p>
      <p className="mt-1 text-[11px] text-muted-foreground">{hint}</p>
    </div>
  );
}
